/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from "react-router-dom";
import { CommonImageInput, CommonInput, PageToolbar, CustomFileSelector } from "../common";
import SimpleEditor from "../common/SimpleEditor";
import helpers from "../../_helpers/common";
import { PageContent } from "../../types/interfaces"
import { createFormErrors, FormError } from '../../utility/formUtils';
import useCommonActions from "../../hooks/useCommonActions";
import useAxios from "../../hooks/useAxios";

type PageContentErrors = Record<string, string>;

const CreateUpdatePage: React.FC = () => {
  const axiosInstance  = useAxios();
  const { product_id } = useParams();
  const { page_id } = useParams();
  const navigate       = useNavigate();
  const { deleteItem } = useCommonActions();
  
  const [ formData, setFormData ] = useState<PageContent>({ title: '', description: '',  banner_image: '',  
                                                           seo_title: '', seo_keyword: '', seo_description: '', status: 1 });
  const [ errors, setErrors ] = useState<Partial<PageContent>>({});
  const [ bannerimage, setBannerImage ] = useState<File | null>(null);
  const [ formSubmitted, setFormSubmitted ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);

  const descriptionRef = useRef<any>(null);
  
  useEffect(() => {
	if(page_id !== '0'){
	  getFormData();
	}
  }, [])
  
  //reset galery and display image error on set
  useEffect(() => {
	if(bannerimage){
		setErrors((prev) => ({
			...prev,
			banner_image: undefined,
		}));
	}
  }, [bannerimage])
  
  const getFormData = async() => {
	try {
	  const response: any = await axiosInstance({
		url: `pages/get-form-data`,
		method: "GET",
		params: {pageId: page_id},
	  });
	  
	  if(!response.error){
		let page = response.data.page;
		setFormData({ ...formData,
		  title: page.title, description: page.description, banner_image: page.bannerpath, 
		  seo_title: page.seo_title, seo_keyword: page.seo_keyword, seo_description: page.seo_description,
		  status: page.status
		})
	  }
	} catch (error: any) {
	  console.log('catch error', error)
	} finally {
	  
	}
  }

  // Handle form input changes
  const handleFormInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement |HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    //console.log('input name', name);
    let clean_value: string | number = value;
    if (name === "discount_type") {
      clean_value = Number(clean_value);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: clean_value,
    }));
	//reset the error
	setErrors((prev) => ({
		...prev,
		[name]: undefined,
	}));
  };
  
  const validate = async (): Promise<boolean> => {
	  // Initialize empty error object using the utility
	  //const newErrors = createFormErrors(formData);
      const newErrors: Partial<PageContentErrors> = {};
	  // Validate fields
	  if (!formData.title) {
		newErrors.title = 'Title is required';
	  }

	  if (!formData.description) {
		newErrors.description = 'Description is required';
	  }


	  if (!formData.status) {
		newErrors.status = 'Status is required';
	  }

	  if (!bannerimage && formData.banner_image === '') {
	    newErrors.banner_image = "Page banner image is required";
	  }
      //console.log('newErrors', newErrors);
	  setErrors(newErrors);

	  // Return true if there are no errors
	  return Object.keys(newErrors).length === 0;
  };

  /*Submit Form*/
  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
	let isValid = await validate();
	if (isValid) {
	  setIsSubmitting(true);
	  var form_data = await prepareSubmitData();
	  try {
		const response: any = await axiosInstance({
			url: "pages/create-update",
			method: "POST",
			data: form_data,
			headers: {
			  'Content-Type': 'multipart/form-data',
			},
			timeout: 40000,
		});
		
		//on product creation success redirect back to dashbaord
		if(!response.error){
		  navigate('/page-list');
		}
	  } catch (error: any) {
		//console.log("validation_error catch", error?.response);

		  if (error?.response?.data?.type === "validation_error") {
			const errorsObj = error.response.data.message.errors as Record<string, string[]>;

			const newErrors: PageContentErrors = {};

			(Object.keys(errorsObj) as Array<keyof PageContent>).forEach((field) => {
			  newErrors[field] = errorsObj[field][0];
			});

			setErrors(newErrors);
		  
		  } else {
			console.log("catch error", error);
		  }
	  } finally {
		setIsSubmitting(false);
	  }
	} 
  }
  
  /*Preapare Submit Form Data*/
  const prepareSubmitData = async () => {
	const form_Data = new FormData();
	if(bannerimage){
	  form_Data.append('banner_image', bannerimage);
    }
	if(page_id !== '0'){
		form_Data.append('_id', page_id);
	}
	
	form_Data.append('title', formData.title);
	form_Data.append('description', formData.description);
	form_Data.append('seo_title', formData.seo_title);
	form_Data.append('seo_keyword', formData.seo_keyword);
	form_Data.append('seo_description', formData.seo_description);
	form_Data.append('status', (formData.status ?? 0).toString());
	return form_Data;
  }
 
  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/*Page toolbar Area Start*/}
      <PageToolbar title={page_id === '0' ? "Create Page" : "Update Page"} />
      {/*Page toolbar Area End*/}
      {/*Page Conent Area Start*/}
      <div className="app-content flex-column-fluid">
        <div className="app-container">
          <form className="form d-flex flex-column flex-lg-row">
            <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-8">
              <div className="card card-flush py-1">
                <div className="card-header">
                  <div className="card-title">
                    <h2>General Info</h2>
                  </div>
                </div>
				
                <div className="card-body pt-0">
				  <div className="py-4">
					  <label className="required form-label">Banner Image</label>
					  <br />
					  <div className="text-center mb-2">
						<CommonImageInput
						  fieldName="display_image"
						  defaultImage={formData.banner_image !== '' ? formData.banner_image : "/assets/media/svg/blank-image.svg"}
						  onImageChange={(file) => setBannerImage(file)}
						/>
					  </div>
					  {errors.banner_image && (
						  <span className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
							{errors.banner_image}
						  </span>
					  )}
					  <div className="text-muted fs-7">
						Set the blog banner image. Only *.png, *.jpg and *.jpeg
						image files are accepted
					  </div>
					
				  </div>
				  <div className="mb-4 fv-row fv-plugins-icon-container">
					<label className="required form-label">Title</label>
					<CommonInput
					  type="text"
					  name="title"
					  placeholder="Page Title"
					  value={formData.title}
					  onChange={handleFormInputChange}
					  error={errors.title}
					/>
				  </div>
                  <div className="mb-4">
					<SimpleEditor
					  label="Page Content"
					  required={true}
					  value={formData.description || ""}
					  onChange={(content) => {
						setFormData((prev) => ({ ...prev, description: content }));

						// reset error
						setErrors((prev) => ({ ...prev, description: undefined }));
					  }}
					  placeholder="Enter Page Content..."
					  height={100}
					  error={errors?.description}
					/>
                    <div className="text-muted fs-7">
                      Set a description to the page.
                    </div>
                  </div>
                </div>
              </div>
			  <div className="fv-row mb-10">
				  <div className="row g-9">
					<div className="col">
					  <div className="card card-flush py-1">

						{/* Header */}
						<div className="card-header d-flex justify-content-between align-items-center">
						  <div className="card-title">
							<h2 className="mb-0">SEO Details</h2>
						  </div>

						  {/* Collapse Button */}
						  <button
							className="btn btn-sm btn-light-primary"
							type="button"
							data-bs-toggle="collapse"
							data-bs-target="#seoDetailsCollapse"
							aria-expanded="true"
							aria-controls="seoDetailsCollapse"
						  >
							Toggle
						  </button>
						</div>

						{/* Body (Collapsable) */}
						<div id="seoDetailsCollapse" className="collapse show">
						  <div className="card-body pt-0">
							<div className="mb-10">
							  <label className="form-label">Meta Tag Title</label>
							  <CommonInput
								type="text"
								name="seo_title"
								placeholder="Meta tag name"
								value={formData.seo_title}
								onChange={handleFormInputChange}
							  />
							</div>

							<div className="mb-10">
							  <label className="form-label">Meta Tag Description</label>
							  <CommonInput
								type="text"
								name="seo_description"
								placeholder="Meta Tag Description"
								value={formData.seo_description}
								onChange={handleFormInputChange}
							  />
							</div>

							<div className="mb-15">
							  <label className="form-label">Meta Tag Keywords</label>
							  <CommonInput
								type="text"
								name="seo_keyword"
								placeholder="Meta Tag Keywords"
								value={formData.seo_keyword}
								onChange={handleFormInputChange}
							  />

							  <div className="text-muted fs-7">
								Set a list of keywords that the product is related to. Separate the keywords by adding a comma{" "}
								<code>,</code> between each keyword.
							  </div>
							</div>
						  </div>
						</div>

					  </div>
					</div>
				  </div>
				</div>
              <div className="d-flex justify-content-end">
                <Link to="/product-list" className="btn btn-light me-5">
                  Cancel
                </Link>
				{isSubmitting ? (
				  <button
					type="button"
					className="btn btn-primary"
					disabled={true}
				  >
					<span
					  className="indicator-progress"
					  style={{ display: "block" }}
					>
					  Please wait...
					  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
					</span>
				  </button>
				) : (
				  <button
					type="button"
					className="btn btn-primary"
					onClick={(e) => submitForm(e)}
				  >
					<span className="indicator-label">Submit</span>
				  </button>
				)}
              </div>
            </div>
          </form>
        </div>
      </div>
      {/*Page Conent Area End*/}
    </div>
  );
};

export default CreateUpdatePage;
